import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { signInWithEmail } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/Input';
import Button from '../components/Button';

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  color: #555;
  font-family: 'Montserrat', sans-serif;
`;

const FormContainer = styled.div`
  width: 100%;
`;

const InfoText = styled.div`
  margin-top: 24px;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
`;

const StyledLink = styled(Link)`
  color: black;
  text-decoration: underline;
  font-weight: 600;
  font-size: 16px;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-bottom: 15px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const ForgotPasswordLink = styled.p`
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || '/dashboard';

  // Check if user is already signed in and redirect to intended destination
  useEffect(() => {
    // Only redirect if auth is not loading and user is authenticated
    if (!authLoading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, from]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setEmail(value);
    } else if (name === 'password') {
      setPassword(value);
    }
    
    // Clear error when user types
    if (error) setError('');
  };

  const handleLogin = async () => {
    // Validate inputs
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    if (!password) {
      setError('Please enter your password');
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setLoading(true);
      await signInWithEmail(email, password);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof Error) {
        if (error.message.includes('user-not-found')) {
          setError('No account found with this email address');
        } else if (error.message.includes('wrong-password')) {
          setError('Invalid password. Please try again');
        } else if (error.message.includes('too-many-requests')) {
          setError('Too many failed attempts. Please try again later');
        } else {
          setError('Login failed: ' + error.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <>
        <Helmet>
          <title>{APP_NAME} - Login</title>
          <meta name="description" content={`${APP_NAME} - Login`} />
        </Helmet>
        <AuthLayout altText="Loading...">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontFamily: 'Montserrat, sans-serif', color: '#666' }}>
              Loading...
            </p>
          </div>
        </AuthLayout>
      </>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Login</title>
        <meta name="description" content={`${APP_NAME} - Login`} />
      </Helmet>
      <AuthLayout 
        altText="Sign in to your account"
      >
      <Title>Welcome Back</Title>
      
      <FormContainer>
        <Input
          type="email"
          name="email"
          value={email}
          onChange={handleInputChange}
          placeholder="Enter your email"
          label="Email Address"
          required
        />
        
        <Input
          type="password"
          name="password"
          value={password}
          onChange={handleInputChange}
          placeholder="Enter your password"
          label="Password"
          required
        />
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Button 
          text="Sign In"
          onClick={handleLogin}
          loading={loading}
          disabled={!email || !password}
        />
        
        <ForgotPasswordLink>
          <StyledLink to="/reset-password">Forgot your password?</StyledLink>
        </ForgotPasswordLink>
      </FormContainer>
        <InfoText>
        Don't have an account? <StyledLink to="/register">Create one</StyledLink>
      </InfoText>
    </AuthLayout>
    </>
  );
};

export default LoginScreen;